import React, { useEffect, useRef, useState } from 'react';
import {
    Box,
    Paper,
    Typography,
    IconButton,
    Button,
    Chip,
} from '@mui/material';
import {
    ChevronLeft,
    ChevronRight,
    Delete as DeleteIcon,
    NoteAdd as NoteAddIcon,
    Draw as DrawIcon,
    ContentCopy as CopyIcon,
    CalendarMonth as CalendarIcon,
    CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import timesheetService from '../../services/timesheetService';

function formatWeekEndingDate(dateStr) {
    const date = new Date(dateStr + 'T00:00:00');
    return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
}

function getWeekdayName(dateStr) {
    const date = new Date(dateStr + 'T00:00:00');
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[date.getDay()];
}

/** «Mon 10/12» — на кнопке копирования дня. */
function shortDayLabel(dateStr) {
    const date = new Date(dateStr + 'T00:00:00');
    return `${getWeekdayName(dateStr).slice(0, 3)} ${date.getMonth() + 1}/${date.getDate()}`;
}

const btnSx = { borderRadius: 2, textTransform: 'none', whiteSpace: 'nowrap' };
// Под палец (айпад, перчатки): стрелки дней 48px, кнопки — от 40px.
const navBtnSx = { width: 48, height: 48, bgcolor: 'grey.200', '&:hover': { bgcolor: 'grey.300' } };
const touchBtnSx = { ...btnSx, minHeight: 40, px: 1.5, fontSize: '0.85rem' };

/** «10/18» — короткая дата для узкой шапки. */
function shortDate(dateStr) {
    const date = new Date(dateStr + 'T00:00:00');
    return `${date.getMonth() + 1}/${date.getDate()}`;
}

// Шапка уже этой ширины — айпад: кнопки сеткой в правой колонке. Меряем
// саму шапку, а не окно: так же работает и с открытым боковым меню.
const COMPACT_WIDTH = 1250;
// Совсем узко (портрет айпада) — без года в дате недели и с узкой плашкой дня.
const TIGHT_WIDTH = 800;

/**
 * Шапка таймшита. День со стрелками — всегда по центру. На ПК — одна строка.
 * На айпаде — три равные колонки на всю высоту: неделя и копирование слева,
 * день (стрелки 48px под палец) по центру, справа 2×2 — основное сверху
 * (Job notes, Sign), удаление снизу и контурное (Clear day / Clear week).
 */
export default function TimesheetHeader({
    currentDate,
    weekInfo,
    weekRecords,
    selectedJobs,
    isReadOnly,
    isWeekFullySigned,
    onNavigateDate,
    onGoToToday,
    onOpenCalendar,
    onClearWeek,
    onClearDay,
    onOpenDailyNotes,
    onSign,
    canCopyLastWeek = false,
    onCopyLastWeek,
    copyDayFrom = null,
    copyDayCount = 0,
    onCopyDay,
    approvedCount = 0,
    totalWeekRecords = 0,
    isWeekFullyApproved = false,
}) {
    const paperRef = useRef(null);
    const [width, setWidth] = useState(0);
    useEffect(() => {
        const el = paperRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return undefined;
        const ro = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
        ro.observe(el);
        return () => ro.disconnect();
    }, []);
    const compact = width > 0 && width < COMPACT_WIDTH;
    const tight = width > 0 && width < TIGHT_WIDTH;

    const bSx = compact ? touchBtnSx : btnSx;

    const weekEndingDateStr = timesheetService.getWeekEndingDate(currentDate);
    const activeRecordsCount = weekRecords.filter(r => !r.isDeleted).length;
    const activeDayRecordsCount = weekRecords.filter(r => !r.isDeleted && r.workDate === currentDate).length;

    // Когда данные этого устройства в последний раз РЕАЛЬНО дошли до офиса
    // (ставится sync-сервисом при успешной отправке батча). Локальное время.
    let dataSentLabel = null;
    try {
        const raw = localStorage.getItem('timesheetLastSentAt');
        if (raw) {
            const d = new Date(raw);
            if (!isNaN(d)) {
                const p = n => String(n).padStart(2, '0');
                dataSentLabel = `${p(d.getMonth() + 1)}/${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
            }
        }
    } catch (e) { /* noop */ }

    const weekNo = weekInfo?.weekNumber || '';
    const endsLabel = `Ends ${tight ? shortDate(weekEndingDateStr) : formatWeekEndingDate(weekEndingDateStr)}`;
    const full = compact ? { width: '100%' } : {};

    // ─── Элементы шапки (одни и те же для ПК и айпада) ──────────────────────

    // Неделя: тап — календарь.
    const weekChip = (
        <Chip
            label={`Week ${weekNo || '-'}`}
            size="small"
            color="success"
            variant="outlined"
            sx={{ height: 22, fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer' }}
        />
    );
    const weekButton = (tall) => (
        <Button
            onClick={onOpenCalendar}
            variant="outlined"
            size="small"
            sx={{
                ...btnSx, ...full, px: 1, py: 0.25, gap: 0.75,
                minHeight: compact ? (tall ? 86 : 40) : undefined,
                flexDirection: tall ? 'column' : 'row',
            }}
            title={`Pick a day · week ends ${formatWeekEndingDate(weekEndingDateStr)}`}
        >
            {weekChip}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CalendarIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">{endsLabel}</Typography>
            </Box>
        </Button>
    );

    // Когда часы этого устройства последний раз дошли до офиса.
    const sentLabel = dataSentLabel && (
        <Typography
            variant="caption"
            sx={{ fontWeight: 600, lineHeight: 1.1, color: 'success.dark', fontSize: '0.68rem', whiteSpace: 'nowrap' }}
            title="Last time this device's hours reached the office server"
        >
            📤 Sent to office: {dataSentLabel}
        </Typography>
    );

    const currentWeekButton = isReadOnly && (
        <Button
            onClick={onGoToToday}
            variant="contained"
            size="small"
            sx={{ ...bSx, ...full }}
            startIcon={<ChevronLeft sx={{ fontSize: 16 }} />}
        >
            Current Week
        </Button>
    );

    // День и стрелки — под палец: 48px.
    const dayNav = (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton onClick={() => onNavigateDate(-1)} aria-label="Previous day" sx={navBtnSx}>
                <ChevronLeft sx={{ fontSize: 32 }} />
            </IconButton>
            <Box sx={{
                px: compact ? 1 : 1.5, py: 0.5, minHeight: 48, boxSizing: 'border-box',
                display: 'flex', flexDirection: 'column', justifyContent: 'center',
                borderRadius: 1.5, bgcolor: 'grey.100', textAlign: 'center', minWidth: tight ? 104 : compact ? 116 : 150,
            }}>
                <Typography sx={{ fontWeight: 700, textTransform: 'uppercase', fontSize: compact ? '0.9rem' : '0.95rem', lineHeight: 1.15 }}>
                    {getWeekdayName(currentDate)}
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', lineHeight: 1.1 }}>
                    {formatWeekEndingDate(currentDate)}
                </Typography>
            </Box>
            <IconButton onClick={() => onNavigateDate(1)} aria-label="Next day" sx={navBtnSx}>
                <ChevronRight sx={{ fontSize: 32 }} />
            </IconButton>
        </Box>
    );

    // Копирование: прошлый день (вт–вс) или прошлая неделя (понедельник, пока нет часов).
    const copyButton = (copyDayFrom && (
        <Button
            onClick={onCopyDay}
            variant="outlined"
            size="small"
            startIcon={<CopyIcon sx={{ fontSize: 16 }} />}
            sx={{ ...bSx, ...full }}
            title={`Copy ${copyDayCount} entr${copyDayCount === 1 ? 'y' : 'ies'} from ${shortDayLabel(copyDayFrom)} into empty cells of this day`}
        >
            Copy {shortDayLabel(copyDayFrom)}
        </Button>
    )) || (canCopyLastWeek && (
        <Button
            onClick={onCopyLastWeek}
            variant="contained"
            color="secondary"
            size="small"
            startIcon={<CopyIcon />}
            sx={{ ...bSx, ...full }}
            title="Add last week's employees and jobs to this week (no hours)"
        >
            Copy last week
        </Button>
    )) || null;

    // Основное — залитые кнопки; удаление — контурные красные, тише и отдельно.
    const notesButton = !isReadOnly && (
        <Button
            onClick={onOpenDailyNotes}
            disabled={selectedJobs.length === 0}
            variant="contained"
            size="small"
            startIcon={<NoteAddIcon />}
            sx={{ ...bSx, ...full }}
            title="Daily job notes"
        >
            {compact ? 'Job notes' : 'Daily job notes'}
        </Button>
    );
    // Подпись — необязательная опция. Напоминаний о неподписанных неделях нет.
    const signedDone = isReadOnly && isWeekFullySigned;
    const signButton = (
        <Button
            onClick={onSign}
            disabled={selectedJobs.length === 0}
            variant={signedDone ? 'outlined' : 'contained'}
            color="success"
            size="small"
            startIcon={signedDone ? <CheckCircleIcon /> : <DrawIcon />}
            sx={{ ...bSx, ...full }}
            title={signedDone ? `Week ${weekNo} is signed — tap to sign again` : `Sign week ${weekNo}`}
        >
            {signedDone ? 'Signed' : (tight ? `Sign W${weekNo}` : `Sign Week ${weekNo}`)}
        </Button>
    );
    const clearDayButton = !isReadOnly && (
        <Button
            onClick={onClearDay}
            disabled={activeDayRecordsCount === 0}
            variant={compact ? 'outlined' : 'contained'}
            color="error"
            size="small"
            startIcon={<DeleteIcon />}
            sx={{ ...bSx, ...full }}
            title="Delete your hours for this day"
        >
            {compact ? 'Clear day' : 'Day'}
        </Button>
    );
    const clearWeekButton = !isReadOnly && (
        <Button
            onClick={onClearWeek}
            disabled={activeRecordsCount === 0}
            variant={compact ? 'outlined' : 'contained'}
            color="error"
            size="small"
            startIcon={<DeleteIcon />}
            sx={{ ...bSx, ...full }}
            title="Delete your hours for the whole week"
        >
            {compact ? 'Clear week' : 'Week'}
        </Button>
    );

    // ─── Айпад: три равные колонки, каждая на всю высоту шапки ──────────────
    //   [ неделя          ]   [ ◀  ДЕНЬ  ▶ ]   [ Job notes | Sign week ]
    //   [ Copy Thu         ]   [ Sent to …  ]   [ Clear day | Clear week ]
    // Копировать нечего — неделя одной высокой карточкой (без дыры слева).
    const leftSecond = copyButton || currentWeekButton;
    const rightButtons = [notesButton, signButton, clearDayButton, clearWeekButton].filter(Boolean);
    const compactLayout = (
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', columnGap: 1.5 }}>
            <Box sx={{ display: 'grid', gap: 0.75, justifySelf: 'stretch', maxWidth: 300 }}>
                {weekButton(!leftSecond)}
                {leftSecond}
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                {dayNav}
                {sentLabel}
            </Box>
            <Box sx={{
                display: 'grid', gridTemplateColumns: rightButtons.length > 1 ? '1fr 1fr' : '1fr', gap: 0.75,
                justifySelf: 'stretch', maxWidth: 360, marginLeft: 'auto', width: '100%',
            }}>
                {rightButtons}
            </Box>
        </Box>
    );

    // ─── ПК: одна строка, день — точно по центру ────────────────────────────
    const wideLayout = (
        <Box sx={{
            display: 'grid',
            gridTemplateColumns: 'minmax(max-content, 1fr) auto minmax(max-content, 1fr)',
            alignItems: 'center', columnGap: 1,
        }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, justifySelf: 'start' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                    {weekButton(false)}
                    {sentLabel && <Box sx={{ pl: 0.5 }}>{sentLabel}</Box>}
                </Box>
                {currentWeekButton}
            </Box>
            {dayNav}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, justifySelf: 'end' }}>
                {copyButton}
                {clearDayButton}
                {clearWeekButton}
                {notesButton}
                {signButton}
            </Box>
        </Box>
    );

    return (
        <Paper ref={paperRef} elevation={1} sx={{ px: compact ? 1.25 : 1.5, py: 1, borderRadius: 2 }}>
            {compact ? compactLayout : wideLayout}

            {/* Апрув для payroll — отдельной тонкой строкой, только когда есть */}
            {approvedCount > 0 && (
                <Box
                    sx={{
                        display: 'flex', alignItems: 'center', gap: 1, mt: 0.75,
                        px: 1.25, py: 0.5,
                        bgcolor: '#ede9fe', border: '1px solid #c4b5fd', borderLeft: '4px solid #7c3aed',
                        borderRadius: 1.5,
                    }}
                    title={isWeekFullyApproved
                        ? "Everything in this week is queued for ADP — editing disabled. Ask the office to unapprove if a correction is required."
                        : "Cells marked 🔒 are locked (already sent to ADP). Other hours are still editable."}
                >
                    <Box sx={{ fontSize: '1rem', lineHeight: 1 }}>🔒</Box>
                    <Typography variant="caption" fontWeight={700} color="#5b21b6" sx={{ lineHeight: 1.2 }}>
                        {isWeekFullyApproved
                            ? 'All hours approved for payroll — locked'
                            : `${approvedCount}/${totalWeekRecords} entries approved for payroll`}
                    </Typography>
                    <Typography variant="caption" color="#6d28d9" sx={{ fontSize: '0.68rem', lineHeight: 1.2 }}>
                        {isWeekFullyApproved
                            ? 'Editing disabled · ask the office to unapprove for corrections'
                            : 'Locked entries keep the 🔒 mark · others remain editable'}
                    </Typography>
                </Box>
            )}
        </Paper>
    );
}
